import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getShippingCompanies, deleteShippingCompany } from "../../../services/Admin/ShippingCompanyesApi";
import ShippingCompanyDialog from "./ShippingCompanyDialog";
import EditShippingCompanyDialog from "./EditShippingCompanyDialog";
import ShippingZonesAndStatesTab from "./ShippingSegmentsAndProvinces/ShippingZonesAndStatesTab";

const ShippingCompanyPage = () => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const fetchCompanies = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getShippingCompanies();
      const data = res && res.success ? res.data : res;
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "فشل تحميل بيانات شركات الشحن");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!company || !company.id) return;
    const confirmed = window.confirm(`هل أنت متأكد من حذف شركة الشحن "${company.name}"؟`);
    if (!confirmed) return;
    setDeletingId(company.id);
    try {
      await deleteShippingCompany(company.id, { company_id: company.id });
      await fetchCompanies();
    } catch (err) {
      // يمكن إضافة عرض خطأ إن رغبت
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">شركات الشحن</Typography>
        <Box>
          <Button sx={{ mr: 1 }} onClick={() => setDialogOpen(true)} size="small" variant="contained">
            إضافة شركة شحن
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={fetchCompanies} disabled={loading} size="small" variant="outlined">
            {loading ? "جارٍ..." : "تحديث"}
          </Button>
        </Box>
      </Box>

      {/* تبويبات العرض */}
      <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label="قائمة شركات الشحن" />
        <Tab label="شرائح ومحافظات الشحن" />
      </Tabs>

      {/* محتوى كل تبويبة */}
      {tabIndex === 0 ? (
        <>
          {/* حوار إضافة شركة شحن */}
          <ShippingCompanyDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSuccess={async () => {
              setDialogOpen(false);
              try {
                setLoading(true);
                const res = await getShippingCompanies();
                const data = res && res.success ? res.data : res;
                setCompanies(Array.isArray(data) ? data : []);
              } catch (err) {
                // ignore
              } finally {
                setLoading(false);
              }
            }}
          />

          {/* حوار تعديل شركة شحن */}
          <EditShippingCompanyDialog
            open={editDialogOpen}
            company={editingCompany}
            onClose={() => { setEditDialogOpen(false); setEditingCompany(null); }}
            onSuccess={async () => {
              setEditDialogOpen(false);
              setEditingCompany(null);
              try {
                setLoading(true);
                const res = await getShippingCompanies();
                const data = res && res.success ? res.data : res;
                setCompanies(Array.isArray(data) ? data : []);
              } catch (err) {
                // ignore
              } finally {
                setLoading(false);
              }
            }}
          />

          {loading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ py: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : companies && companies.length ? (
            <Paper>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>الـ ID</TableCell>
                    <TableCell>الاسم</TableCell>
                    <TableCell>العنوان</TableCell>
                    <TableCell>الهاتف</TableCell>
                    <TableCell>البريد</TableCell>
                    <TableCell>تاريخ الإنشاء</TableCell>
                    <TableCell>إجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.id}</TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.address}</TableCell>
                      <TableCell>{c.phone}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{formatDate(c.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => { setEditingCompany(c); setEditDialogOpen(true); }}
                        >
                          تعديل
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteCompany(c)}
                          disabled={deletingId === c.id || loading}
                          sx={{ ml: 1 }}
                        >
                          حذف
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Typography color="text.secondary">لا توجد بيانات</Typography>
          )}
        </>
      ) : (
        <ShippingZonesAndStatesTab />
      )}
    </Box>
  );
};

export default ShippingCompanyPage;
